import { Button, ButtonIcon } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import { Dropdown, type DropdownItem } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import { Base } from "@/atoms-react/text/Text.react";
import type { DeclarativeBirth, DeclarativeNif } from "@/features/flow/types";
import { useUser } from "@/features/user/hooks/use-user";
import { useNifValidation } from "@/features/user/nif/hooks/use-nif-validation";
import type { FullCountry } from "@/features/user/types";
import { COUNTRY_NIF_CHECK_REQUIRED, FAVOURITE_COUNTRY_CODES } from "@/features/utils/constants";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { FlowHeader } from "@/flow/components/flow-header.react";
import { useAppDispatch } from "@/redux/hooks";
import { setIsInPreviousStep } from "@/redux/slices/flowSlice";
import { setDeclarativeIdentityTaxAdress } from "@/redux/slices/signupSlice";
import { Themes } from "@/types/theme";
import { hasDuplicates } from "@/utils/array.utils";
import { ChevronRightIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface DeclarativeNifProps extends LayoutBaseProps {
  onValidated?: (output: DeclarativeNif[]) => void;
  declarativePerson?: DeclarativeBirth;
  personNif?: DeclarativeNif[];
}

function DeclarativeNifTab({
  onValidated = () => {},
  personNif,
  declarativePerson
}: DeclarativeNifProps) {
  const { locale } = useLocale();
  const dispatch = useAppDispatch();
  const { getNifValidation } = useNifValidation();
  const { mutateAsync: nififValidation, isPending } = getNifValidation();
  const { useCountries } = useUser();
  const { data: countries } = useCountries(locale.language);
  const { t } = useTranslation(["pass"]);
  const values = {
    personNif:
      !personNif || personNif?.length === 0
        ? [
            {
              country: null,
              apiCheck: false,
              tin: null,
              tinCheck: false,
              tinExist: false
            }
          ]
        : personNif?.map((declarativeNif) => declarativeNif)
  };

  const { control, handleSubmit, getValues } = useForm({
    values
  });
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "personNif",
    rules: { maxLength: 4 }
  });

  const [error, setError] = useState<string[] | null>([]);

  const [addressNifToggle, setAddressNifToggle] = useState<boolean | null>(false);
  const handleNifVerification = async (taxResidence: DeclarativeNif) => {
    const countryBirthIsMalta = countries?.countries.find(
      (item: FullCountry) =>
        item.id === parseInt(declarativePerson?.countryCode ?? "") && item.alpha2 === "MT"
    );
    const maltaCountryCode = countries?.countries.find(
      (item: FullCountry) => item.alpha2.toLocaleUpperCase() === "MT"
    );
    const taxNifCountry = countries?.countries.find(
      (item: FullCountry) => item.id.toString() === taxResidence.country
    );
    const arrCountry = fields.map((field) => field.country);
    if (arrCountry.filter((ad) => ad === taxResidence.country).length > 1) {
      toast.error(t("flow.id_control.input.nationality_add_error"));
      return;
    }
    if (taxResidence.country && (taxResidence.tin === null || taxResidence.tin === "")) {
      return;
    }
    if (
      COUNTRY_NIF_CHECK_REQUIRED.includes(taxNifCountry?.alpha2 as string) &&
      ((taxResidence.country as string) !== maltaCountryCode?.id.toString() ||
        ((taxResidence.country as string) === maltaCountryCode?.id.toString() &&
          countryBirthIsMalta))
    ) {
      const response = await nififValidation({
        nif: taxResidence.tin as string,
        countryCode: taxNifCountry?.alpha2 as string
      });
      if (response.validSyntax && response.validStructure) {
        // to fix "can't assign to readonly property" error
        taxResidence = {
          ...taxResidence,
          tinCheck: true,
          apiCheck: true,
          tinExist: response.validSyntax && response.validStructure
        };
      } else {
        setError((prev) => [...(prev as string[]), taxNifCountry?.id?.toString() ?? ""]);
        toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error"));
      }
    } else {
      // to fix "can't assign to readonly property" error
      taxResidence = { ...taxResidence, tinCheck: true, apiCheck: true, tinExist: true };
    }
    return taxResidence;
  };

  const execute = async (personsNifSubmitted: DeclarativeNif[]) => {
    setError([]);
    if (hasDuplicates(personsNifSubmitted.map((field) => field.country))) {
      toast.error(t("flow.id_control.input.nationality_add_error"));
      return;
    }
    if (personsNifSubmitted.length === 0 || !personsNifSubmitted[0].country) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }

    const asyncVerification = personsNifSubmitted.map(
      async (ta: DeclarativeNif) => await handleNifVerification(ta)
    );

    const arrAsync = await Promise.all(asyncVerification);
    const err = arrAsync.filter((ta) => ta?.tin && !ta?.apiCheck);

    if (err?.length === 0) onValidated(personsNifSubmitted);
  };

  const handleAutoComplete = (
    autoComplete: string,
    items: DropdownItem[],
    change: (item: DropdownItem) => void
  ) => {
    const country = (countries?.countries ?? []).find(
      (country) => country.alpha2.toLowerCase() === autoComplete.toLowerCase()
    );
    const autocompletedValue = items.find((item) => item?.key === country?.id.toString()) ?? null;

    if (autocompletedValue) change(autocompletedValue);
  };

  const items =
    (countries?.countries ?? []).map((country) => ({
      key: country.id.toString(),
      value: country.defaultName,
      icon: <Flag countryCode={country.alpha2} />,
      render: ({ key }: { key: string }) => (
        <div className={"flex w-full"} key={key}>
          <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
          <Base className={"max-8 ml-2 truncate"}>{country.defaultName}</Base>
        </div>
      ),
      groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
        ? "default"
        : undefined
    })) ?? [];

  return (
    <form onSubmit={handleSubmit(async (data) => await execute(data.personNif))}>
      <Column className="items-center justify-between">
        <div className="max-h[500px] mb-12">
          <FlowHeader
            title={t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.step_title")}
            back={true}
            onPreviousClick={() => {
              dispatch(setIsInPreviousStep(true));
              const formValue = getValues();
              dispatch(setDeclarativeIdentityTaxAdress(formValue.personNif));
            }}
          />
          <Base className="mb-6 font-semibold">
            {" "}
            {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.title")}
          </Base>
          <Base className="mt-4 text-sm">
            {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.explication")}
          </Base>
          <Column>
            {fields.map((field, index) => {
              return (
                <div className="mt-4 flex flex-row" key={field.id}>
                  <Dropdown
                    placeholder={`${t("flow.id_control.input.business.country_label")} *`}
                    onChange={(value) => {
                      update(index, {
                        ...fields[index],
                        country: value.key,
                        apiCheck: false,
                        tin: null,
                        tinCheck: false,
                        tinExist: false
                      });
                    }}
                    items={items}
                    value={field?.country}
                    autoComplete="country"
                    handleAutoComplete={handleAutoComplete}
                    groups={[{ key: "default" }]}
                    search={true}
                    className="w-40 min-w-40 max-w-40"
                  />

                  <Controller
                    control={control}
                    name={`personNif.${index}.tin`}
                    render={({ field: { onChange, onBlur, name, value, ref } }) => {
                      if (value === undefined) value = "";
                      return (
                        <InputLabel
                          required={false}
                          dataCy="input-nif"
                          autoComplete="given-name"
                          placeholder={t("olkypass.accountPage.basicInfo.input.nif")}
                          className={`ml-1 w-full`}
                          error={
                            (field?.tinExist && field?.tin?.length === 0 && !field.apiCheck) ||
                            error?.includes(field?.country?.toString() as string)
                              ? t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error")
                              : undefined
                          }
                          disabled={
                            isPending ||
                            (field.country !== null &&
                              field.country.length > 0 &&
                              field.apiCheck === true &&
                              field.tinCheck === true &&
                              field.tinExist === true)
                          }
                          onChange={(newTinValue) => onChange(newTinValue.toUpperCase())}
                          value={value ?? ""}
                          onBlur={onBlur}
                          name={name}
                          ref={ref}
                        />
                      );
                    }}
                  />

                  {index > 0 && (
                    <div className="ml-2 flex flex-col justify-center">
                      <ButtonIcon
                        size="md"
                        theme={Themes.white}
                        element={<TrashIcon />}
                        className="ml-2 cursor-pointer text-red-600"
                        onClick={() => {
                          remove(index);
                          setAddressNifToggle(false);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {fields[fields.length - 1]?.country && fields.length < 4 && (
              <div className="mt-2 mb-2">
                <Base className="mt-4 w-full text-base">
                  {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.check")}
                </Base>
                <div className="mt-4 flex w-full flex-row">
                  <Base className="px-3 text-base">
                    {" "}
                    {t("flow.id_control.input.add_nationality_checkbox_yes")}
                  </Base>
                  <Checkbox
                    value={addressNifToggle === true}
                    onChange={() => setAddressNifToggle(true)}
                  />
                  <Checkbox
                    className="ml-4"
                    value={addressNifToggle === false}
                    onChange={() => setAddressNifToggle(false)}
                  >
                    <Base className="text-base">
                      {" "}
                      {t("flow.id_control.input.add_nationality_checkbox_no")}
                    </Base>
                  </Checkbox>
                </div>
              </div>
            )}

            {addressNifToggle === true && (
              <>
                <div className="mt-2 flex flex-row">
                  <Dropdown
                    placeholder={`${t("flow.id_control.input.business.country_label")} *`}
                    onChange={(value) => {
                      append({
                        country: value.key,
                        apiCheck: false,
                        tin: null,
                        tinCheck: false,
                        tinExist: false
                      });
                      setAddressNifToggle(false);
                    }}
                    value={null}
                    items={items}
                    autoComplete="country"
                    handleAutoComplete={handleAutoComplete}
                    groups={[{ key: "default" }]}
                    search={true}
                    className="w-40 min-w-40 max-w-40"
                  />

                  <InputLabel
                    dataCy="input-nif"
                    autoComplete="given-name"
                    placeholder={t("olkypass.accountPage.basicInfo.input.nif")}
                    className={`ml-1 w-full`}
                    value={""}
                    disabled={true}
                  />
                </div>
              </>
            )}
          </Column>
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <DotsNavigation total={8} current={4} />
          <Button
            shortcut="enter"
            icon={<ChevronRightIcon />}
            className="my-4"
            disabled={addressNifToggle === true || isPending}
            isSubmit={true}
            onSubmit={() => dispatch(setIsInPreviousStep(false))}
          >
            {t("flow.id_control.input.continue")}
          </Button>
        </div>
      </Column>
    </form>
  );
}

export default DeclarativeNifTab;
